import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  QBox,
  QButton,
  QFormControl,
  QInput,
  QMenuButton,
  QMenuItem,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QRadio,
  QRadioGroup,
  QSelect,
  QSelectPlaceholder,
  QStack,
  QText,
  useFeatureFlag,
  useToastProvider,
} from '@qualio/ui-components';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';

import { changeControlApi } from '../../../api/changeControl';
import { documentApi } from '../../../api/document';
import { DocumentFormat } from '../../../api/model/document';
import { Template } from '../../../api/templateApi';

import { useCCWorkflowData } from '../../../hooks/useCCWorkflowData';
import {
  useInvalidateNextAvailableDocumentCode,
  useNextAvailableDocumentCode,
} from '../../../hooks/useNextAvailableDocumentCode';
import { useInvalidateTemplates } from '../../../hooks/useTemplates';
import {
  CCWorkflowLink,
  ChangeControlDTO,
  DocumentPropertiesDTO,
  PropertiesForm,
} from '../../../types/DocumentCreate';
import { getApiErrorData } from '../../../util/ApiErrorUtils';
import {
  DOCUMENT_CODE_MAX_LENGTH,
  getDocumentCodeErrorMessageKey,
  getDocumentCodeErrorSlug,
} from '../../../util/DocumentCodeUtils';
import { filterOptionBySearchText } from '../../../util/SelectionOptionUtils';
import { useInheritedTemplateOptions } from '../InheritedTemplateIcon/useInheritedTemplateOptions';
import { getTemplatesOptions } from './utils';

type CCWorkflowSelection = 'select_now' | 'select_later';

const RESOURCE_NOT_FOUND_SLUG = '404_not_found';

export const NewDocumentButton = ({
  currentUser,
  templates,
}: {
  currentUser: CurrentUser;
  templates: Template[];
}) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const featureFlags = useFeatureFlag();
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);
  const useCustomDocumentCodes = Boolean(featureFlags['custom-document-codes']);

  const navigate = useNavigate();
  const invalidateTemplates = useInvalidateTemplates(currentUser.companyId);

  const [isOpen, setIsOpen] = useState(false);

  // CC workflow picker state
  const [selectedDocTemplateId, setSelectedDocTemplateId] = useState<
    number | null
  >(null);
  const [ccWorkflowSelection, setCcWorkflowSelection] =
    useState<CCWorkflowSelection>('select_now');
  const [selectedCCWorkflowId, setSelectedCCWorkflowId] = useState<
    number | null
  >(null);

  const [isCreatingDoc, setIsCreatingDoc] = useState(false);
  const [templateSelectResetKey, setTemplateSelectResetKey] = useState(0);

  const {
    allCCTemplates,
    linkedCCWorkflows,
    isLoadingLinkedWorkflows,
    isLoadingAllCCTemplates,
  } = useCCWorkflowData(currentUser.companyId, selectedDocTemplateId, true);

  // Fallback CC template when no workflow is explicitly selected: qualio_managed, else first available
  const systemCCTemplate =
    allCCTemplates.find((t) => t.qualio_managed) ?? allCCTemplates[0] ?? null;
  const changeControlTemplateId = systemCCTemplate
    ? Number(systemCCTemplate.id)
    : null;
  const changeControl: ChangeControlDTO | null = systemCCTemplate
    ? { sections: systemCCTemplate.sections }
    : null;

  const effectiveTemplates = useMemo(
    () => templates.filter((temp) => temp.status === 'effective'),
    [templates],
  );

  const baseTemplateOptions = useMemo(
    () => getTemplatesOptions(effectiveTemplates),
    [effectiveTemplates],
  );
  const templateOptions = useInheritedTemplateOptions(baseTemplateOptions);

  const handleSaveDocumentError = (e: any) => {
    setIsCreatingDoc(false);
    const { slug } = getApiErrorData(e);

    const documentCodeErrorMessageKey = useCustomDocumentCodes
      ? getDocumentCodeErrorMessageKey(slug)
      : undefined;
    const isTemplateNoLongerAvailable =
      enterpriseAccounts && slug === RESOURCE_NOT_FOUND_SLUG;

    if (isTemplateNoLongerAvailable) {
      clearTemplateSelection();
      void invalidateTemplates();
    }

    let errorDescription = t('documentActions.failedToCreateNewDocument');
    if (documentCodeErrorMessageKey) {
      errorDescription = t(documentCodeErrorMessageKey, {
        max: DOCUMENT_CODE_MAX_LENGTH,
      });
    } else if (isTemplateNoLongerAvailable) {
      errorDescription = t('documentActions.documentTypeNoLongerAvailable');
    }

    showToast({
      'data-cy': 'new-document-create-error-toast',
      id: 'create-document-error',
      status: 'error',
      title: t('notifications.cannotCreateNewDocument'),
      description: errorDescription,
      replace: true,
    });
  };

  const handleChangeTemplatesWorkflow = async (document: any) => {
    if (ccWorkflowSelection === 'select_now') {
      const templateId =
        selectedCCWorkflowId ?? (changeControlTemplateId as number);
      const template = allCCTemplates.find((t) => Number(t.id) === templateId);
      const ccData = template
        ? { sections: template.sections }
        : (changeControl as ChangeControlDTO);
      await changeControlApi.createChangeControl(document, templateId, ccData);
    }
  };

  const saveDocument: SubmitHandler<PropertiesForm> = async (data) => {
    if (!data.title || !data.format || !data.type) {
      return;
    }
    setIsCreatingDoc(true);

    const documentProperties: DocumentPropertiesDTO = {
      title: data.title,
      ...data.type,
      document_format: data.format,
      company_id: currentUser.companyId,
      ...(useCustomDocumentCodes && data.code ? { code: data.code } : {}),
    };

    let newDocument: any;

    try {
      newDocument = await documentApi.createDraft(
        currentUser.companyId,
        documentProperties,
      );
    } catch (e: any) {
      handleSaveDocumentError(e);
      return;
    }

    if (useCustomDocumentCodes) {
      void invalidateNextAvailableDocumentCode();
    }

    await handleChangeTemplatesWorkflow(newDocument);

    void navigate(`/workspace/e/${newDocument.id}?intent=edit`);
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
    resetField,
  } = useForm<PropertiesForm>({
    defaultValues: {
      title: '',
      type: null,
      format: DocumentFormat.QualioNativeDocument,
      code: '',
    },
  });

  const {
    data: nextAvailableDocumentCode,
    isLoading: isLoadingNextAvailableDocumentCode,
  } = useNextAvailableDocumentCode(
    currentUser.companyId,
    selectedDocTemplateId,
    Boolean(selectedDocTemplateId) && useCustomDocumentCodes,
  );
  const invalidateNextAvailableDocumentCode =
    useInvalidateNextAvailableDocumentCode(
      currentUser.companyId,
      selectedDocTemplateId,
    );

  const validateCode = (code: string) => {
    const templatePrefix = effectiveTemplates.find(
      ({ id }) => id === selectedDocTemplateId,
    )?.prefix;
    const errorSlug = getDocumentCodeErrorSlug(code, templatePrefix);
    const messageKey = getDocumentCodeErrorMessageKey(errorSlug);
    return messageKey ? t(messageKey, { max: DOCUMENT_CODE_MAX_LENGTH }) : true;
  };

  // Show linked workflows; fall back to system default only when none are linked
  const ccWorkflowOptions = useMemo(() => {
    const systemTemplate = allCCTemplates.find(
      (t) => t.qualio_managed && t.status === 'effective',
    );
    const linked = linkedCCWorkflows ?? [];

    if (linked.length === 0) {
      return systemTemplate
        ? [
            {
              label: `${systemTemplate.name} (Default)`,
              value: String(systemTemplate.id),
            },
          ]
        : [];
    }

    return linked.map((wf: CCWorkflowLink) => ({
      label: wf.qualio_managed ? `${wf.name} (Default)` : wf.name,
      value: wf.id.toString(),
    }));
  }, [linkedCCWorkflows, allCCTemplates]);

  useEffect(() => {
    if (isLoadingLinkedWorkflows) return;
    if (
      ccWorkflowOptions?.length === 1 &&
      ccWorkflowSelection === 'select_now'
    ) {
      setSelectedCCWorkflowId(Number(ccWorkflowOptions[0]?.value));
    } else {
      setSelectedCCWorkflowId((prev) => {
        if (prev === null) return null;
        return ccWorkflowOptions?.some((o) => o.value === prev.toString())
          ? prev
          : null;
      });
    }
  }, [ccWorkflowOptions, ccWorkflowSelection, isLoadingLinkedWorkflows]);

  const effectiveCCWorkflowValue = selectedCCWorkflowId?.toString();

  const clearTemplateSelection = () => {
    resetField('type');
    setSelectedDocTemplateId(null);
    setCcWorkflowSelection('select_now');
    setSelectedCCWorkflowId(null);
    setTemplateSelectResetKey((key) => key + 1);
  };

  const handleCloseModal = () => {
    reset();
    clearTemplateSelection();
    setIsOpen(false);
  };

  const handleCreateDocument = handleSubmit(saveDocument);

  // disable if user has chosen 'select now' without picking a workflow
  const isCCWorkflowRequired =
    ccWorkflowSelection === 'select_now' &&
    Boolean(selectedDocTemplateId) &&
    !selectedCCWorkflowId;

  const { onChange, ...titleProps } = register('title', {
    required: t('newDocument.titleRequired'),
  });
  const handleTitleChange = (event: SyntheticEvent) => {
    void onChange(event);
    void trigger('title');
  };

  if (!currentUser.permissions.can_author_doc) {
    return null;
  }

  return (
    <>
      <QMenuButton buttonLabel={t('buttons.new')} data-cy="createNewDocMenu">
        <QMenuItem
          key="create-new-doc"
          data-cy="createNewDocButton"
          onClick={() => setIsOpen(true)}
          fontSize={'sm'}
        >
          {t('labels.document')}
        </QMenuItem>
        <QMenuItem
          key="bulk-import-docs"
          data-cy="bulkFileUploadButton"
          onClick={() => navigate('/upload')}
          fontSize={'sm'}
        >
          {t('labels.import')}
        </QMenuItem>
      </QMenuButton>
      <QModal size={'xl'} isOpen={isOpen} onClose={handleCloseModal}>
        <QModalHeader>
          <QText>{t('buttons.createDocument')}</QText>
        </QModalHeader>
        <QModalBody>
          <QStack spacing={5}>
            <QFormControl
              label={t('labels.title')}
              isInvalid={Boolean(errors.title)}
              error={errors.title?.message as any}
            >
              <QInput
                placeholder={t('forms.documentTitle')}
                data-cy="document-title"
                onChange={handleTitleChange}
                {...titleProps}
              />
            </QFormControl>
            <QFormControl
              label={t('labels.type')}
              isInvalid={Boolean(errors.type)}
              error={errors.type?.message as any}
            >
              <QStack spacing={3}>
                <Controller
                  name="type"
                  control={control}
                  rules={{
                    required: t('newDocument.documentTypeRequired'),
                  }}
                  render={({ field: { onChange, value } }) => {
                    // type of selected is TemplateSelectOption
                    // but the onChange of QSelect doesn't
                    // accept an object as a type
                    const handleChange = (selected: any) => {
                      onChange(selected.properties);
                      void trigger('type');
                      setSelectedDocTemplateId(selected.properties.id);
                      setCcWorkflowSelection('select_now');
                      setSelectedCCWorkflowId(null);
                    };
                    return (
                      <QBox data-cy="template-selector">
                        <QSelect
                          key={templateSelectResetKey}
                          aria-label="documentType"
                          size="sm"
                          options={templateOptions}
                          filterOption={filterOptionBySearchText}
                          onChange={handleChange}
                          value={value?.id?.toString()}
                          isLoading={!templateOptions.length}
                          isInvalid={Boolean(errors.type)}
                        >
                          <QSelectPlaceholder>
                            <QText>{t('forms.selectADocumentType')}</QText>
                          </QSelectPlaceholder>
                        </QSelect>
                      </QBox>
                    );
                  }}
                />
              </QStack>
            </QFormControl>
            {useCustomDocumentCodes && (
              <QFormControl
                label={t('labels.id')}
                helper={t('forms.documentCodeHelper', {
                  max: DOCUMENT_CODE_MAX_LENGTH,
                })}
                isInvalid={Boolean(errors.code)}
                error={errors.code?.message as any}
              >
                <QInput
                  {...register('code', { validate: validateCode })}
                  placeholder={
                    nextAvailableDocumentCode ?? t('forms.documentIdentifier')
                  }
                  data-cy="document-code"
                  isDisabled={isLoadingNextAvailableDocumentCode}
                />
              </QFormControl>
            )}
            {selectedDocTemplateId && (
              <QFormControl label={t('newDocument.changeControl')}>
                <QRadioGroup
                  value={ccWorkflowSelection}
                  onChange={(val) => {
                    setCcWorkflowSelection(val as CCWorkflowSelection);
                    setSelectedCCWorkflowId(null);
                  }}
                >
                  <QStack spacing={4}>
                    <QStack spacing={2}>
                      <QRadio value="select_now">
                        <QText>{t('newDocument.ccSelectNow')}</QText>
                      </QRadio>
                      <QSelect
                        key={ccWorkflowSelection}
                        aria-label="changeControlWorkflow"
                        data-cy="cc-workflow-selector"
                        size="sm"
                        options={ccWorkflowOptions}
                        onChange={(selected: any) =>
                          setSelectedCCWorkflowId(Number(selected.value))
                        }
                        value={effectiveCCWorkflowValue}
                        isDisabled={ccWorkflowSelection === 'select_later'}
                      >
                        <QSelectPlaceholder>
                          <QText>
                            {t('newDocument.ccWorkflowPlaceholder')}
                          </QText>
                        </QSelectPlaceholder>
                      </QSelect>
                    </QStack>
                    <QRadio value="select_later">
                      <QText>{t('newDocument.ccSelectLater')}</QText>
                    </QRadio>
                  </QStack>
                </QRadioGroup>
              </QFormControl>
            )}
            <QFormControl label={t('newDocument.fileType')}>
              <Controller
                name="format"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <QRadioGroup onChange={onChange} value={value}>
                    <QStack spacing={6}>
                      <QStack spacing={1}>
                        <QRadio value={DocumentFormat.QualioNativeDocument}>
                          <QText>{t('labels.newDocument')}</QText>
                        </QRadio>
                        <QText color={'gray.500'} pl={6} fontSize={'sm'}>
                          {t('newDocument.createNewControlledContent')}
                        </QText>
                      </QStack>
                      <QStack spacing={1}>
                        <QRadio value={DocumentFormat.DocumentUpload}>
                          <QText>
                            {t('newDocument.uploadFileDescription')}
                          </QText>
                        </QRadio>
                        <QText pl={6} color={'gray.500'} fontSize={'sm'}>
                          {t('newDocument.controlExistingFile')}
                        </QText>
                      </QStack>
                    </QStack>
                  </QRadioGroup>
                )}
              />
            </QFormControl>
          </QStack>
        </QModalBody>
        <QModalActions>
          <QButton
            data-cy={'cancel-create-button'}
            variant="outline"
            isDisabled={isCreatingDoc}
            onClick={handleCloseModal}
          >
            {t('buttons.cancel')}
          </QButton>
          <QButton
            data-cy={'confirm-create-button'}
            onClick={handleCreateDocument}
            isDisabled={isCreatingDoc || isCCWorkflowRequired}
            isLoading={isCreatingDoc || isLoadingAllCCTemplates}
          >
            {t('labels.create')}
          </QButton>
        </QModalActions>
      </QModal>
    </>
  );
};
